import { RefreshIcon } from "@/assets/icons";
import type { IOptionItem } from "@/components/ui/dropdown/types";
import InfoNote from "@/components/ui/InfoNote";
import ToneAlert from "@/components/ui/ToneAlert";
import { DATA_SETTINGS_MESSAGE } from "@/constants/message";
import { AdmAreaMappingOutcome } from "@/hooks/dataset-managment/useAdmAreaMapping";

import { Button } from "equator-design-system";

import AttributeSelection from "../AttributeSelection";
import ViewHeader from "./ViewHeader";

const TEST_ID_PREFIX = "dataset-management-adm-area-mapping-view";

type AdmAreaMappingViewProps = {
  attributesForAdminArea: IOptionItem[];
  selectedAttribute?: string;
  /** The dataset already has a mapping, so a save replaces it. */
  isEditing?: boolean;
  outcome: AdmAreaMappingOutcome;
  isValidating: boolean;
  isConfirmingPartial: boolean;
  onSelectAttribute: (attributeId: string) => void;
  onBack: () => void;
  onCancel: () => void;
  onClose: () => void;
  /** First attempt, and the Retry after a failure. */
  onMap: () => void;
  /** Accepts a partial match: the same request again, with the confirm flag. */
  onConfirmPartial: () => void;
};

const AdmAreaMappingView = ({
  attributesForAdminArea,
  selectedAttribute,
  isEditing = false,
  outcome,
  isValidating,
  isConfirmingPartial,
  onSelectAttribute,
  onBack,
  onCancel,
  onClose,
  onMap,
  onConfirmPartial
}: AdmAreaMappingViewProps) => {
  const isPartial = outcome?.kind === "partial";
  const isFailed = outcome?.kind === "failed";
  // A request is running, so leaving would abandon a save already under way.
  const isBusy = isValidating || isConfirmingPartial;

  const getActionLabel = () => {
    if (isConfirmingPartial) return "Saving…";
    if (isPartial) return "Save Anyway";
    if (isFailed) return "Retry";
    return isEditing ? "Update" : "Save";
  };

  return (
    <div className="flex flex-col">
      <ViewHeader
        title={DATA_SETTINGS_MESSAGE.ADM_AREA_TITLE}
        isDisabled={isBusy}
        onBack={onBack}
        onClose={onClose}
      />

      <span className="text-xs text-dim-gray mt-2">
        {DATA_SETTINGS_MESSAGE.ADM_AREA_DESCRIPTION}
      </span>

      <AttributeSelection
        items={attributesForAdminArea}
        selectedValue={selectedAttribute ?? ""}
        onSelect={onSelectAttribute}
      />

      {isPartial && (
        <ToneAlert
          tone="warning"
          title={DATA_SETTINGS_MESSAGE.ADM_AREA_PARTIAL_TITLE}
          footnote={DATA_SETTINGS_MESSAGE.ADM_AREA_PARTIAL_FOOTNOTE}
          className="mt-4"
          data-testid={`${TEST_ID_PREFIX}-partial-alert`}
        >
          {DATA_SETTINGS_MESSAGE.ADM_AREA_PARTIAL_DESCRIPTION(
            outcome.counts.success,
            outcome.counts.total
          )}
        </ToneAlert>
      )}

      {isFailed && (
        <ToneAlert
          tone="error"
          title={DATA_SETTINGS_MESSAGE.ADM_AREA_FAILED_TITLE}
          footnote={DATA_SETTINGS_MESSAGE.ADM_AREA_FAILED_FOOTNOTE}
          className="mt-4"
          data-testid={`${TEST_ID_PREFIX}-error-alert`}
        >
          {DATA_SETTINGS_MESSAGE.ADM_AREA_FAILED_DESCRIPTION}
        </ToneAlert>
      )}

      {isEditing ? (
        <InfoNote
          variant="muted"
          className="mt-4"
          title={DATA_SETTINGS_MESSAGE.ADM_AREA_EDIT_NOTE_TITLE}
          message={DATA_SETTINGS_MESSAGE.ADM_AREA_ATTRIBUTE_NOTE}
          data-testid={`${TEST_ID_PREFIX}-edit-note`}
        />
      ) : (
        <InfoNote
          variant="muted"
          className="mt-4"
          message={DATA_SETTINGS_MESSAGE.ADM_AREA_ATTRIBUTE_NOTE}
          data-testid={`${TEST_ID_PREFIX}-note`}
        />
      )}

      {isValidating ? (
        <div className="flex flex-col items-center gap-2 pt-4">
          <Button
            className="h-[40px] w-full btn-primary text-sm !font-bold"
            disabled
            data-testid={`${TEST_ID_PREFIX}-validating-button`}
          >
            <RefreshIcon className="w-4 h-4 animate-spin fill-white mr-2" />
            <span>Validating…</span>
          </Button>
          <span className="text-xs text-dim-gray">
            {DATA_SETTINGS_MESSAGE.ADM_AREA_VALIDATING_NOTE}
          </span>
        </div>
      ) : (
        <div className="flex gap-2 w-full pt-4">
          <Button
            className="h-[40px] w-full btn-secondary text-sm !font-bold"
            disabled={isBusy}
            onClick={onCancel}
            data-testid={`${TEST_ID_PREFIX}-cancel-button`}
          >
            <span>Cancel</span>
          </Button>
          <Button
            className="h-[40px] w-full btn-primary text-sm !font-bold"
            type="submit"
            disabled={!selectedAttribute || isConfirmingPartial}
            onClick={isPartial ? onConfirmPartial : onMap}
            data-testid={`${TEST_ID_PREFIX}-save-button`}
          >
            <span>{getActionLabel()}</span>
          </Button>
        </div>
      )}
    </div>
  );
};

export default AdmAreaMappingView;
